import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ZOHO_WEBFORM } from '../data/mcp'
import { trackEvent } from '../utils/analytics'

export type WaitlistPreviewState = 'live' | 'success' | 'error'
export type WaitlistField = 'firstName' | 'lastName' | 'email' | 'phone' | 'company' | 'leadType'
export type WaitlistStatus = 'idle' | 'submitting' | 'success' | 'error'

const FIELD_ORDER: WaitlistField[] = ['firstName', 'lastName', 'email', 'phone', 'company', 'leadType']
const EMPTY: Record<WaitlistField, string> = { firstName: '', lastName: '', email: '', phone: '', company: '', leadType: '' }
const TIMEOUT_MS = 15000

function validate(values: Record<WaitlistField, string>) {
  const errors: Partial<Record<WaitlistField, string>> = {}
  if (!values.firstName.trim()) errors.firstName = 'Enter your first name.'
  if (!values.lastName.trim()) errors.lastName = 'Enter your last name.'
  if (!values.email.trim()) errors.email = 'Enter your email.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) errors.email = 'Enter a valid email address.'
  if (!values.leadType) errors.leadType = 'Choose the option that best describes you.'
  return errors
}

type ZohoWindow = Window & { $zoho?: { salesiq?: { visitor?: { uniqueid?: () => string } } } }

/** Loads the Zoho form analytics script once ("Do not remove" in the embed). */
function useZohoAnalytics() {
  useEffect(() => {
    if (document.getElementById('wf_anal')) return
    const script = document.createElement('script')
    script.id = 'wf_anal'
    script.src = ZOHO_WEBFORM.analyticsSrc
    script.async = true
    document.body.appendChild(script)
  }, [])
}

/**
 * MCP waitlist form posting to Zoho Web-to-Lead through a hidden iframe.
 * Zoho's response is cross-origin, so success is shown once Zoho returns its
 * response page; it can't be read to confirm the lead was created.
 */
export function useMcpWaitlist(idPrefix: string, preview: WaitlistPreviewState = 'live') {
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState<Partial<Record<WaitlistField, string>>>({})
  const [status, setStatus] = useState<WaitlistStatus>('idle')
  const submittingRef = useRef(false)
  const timeoutRef = useRef<number | undefined>(undefined)
  const formRef = useRef<HTMLFormElement>(null)
  useZohoAnalytics()

  useEffect(() => () => window.clearTimeout(timeoutRef.current), [])

  const fieldId = (field: WaitlistField) => `${idPrefix}-${field}`

  const setField = (field: WaitlistField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    if (errors[field]) setErrors(({ [field]: _removed, ...rest }) => rest)
  }

  const finish = (next: 'success' | 'error') => {
    submittingRef.current = false
    window.clearTimeout(timeoutRef.current)
    setStatus(next)
    if (next === 'success') trackEvent('mcp_waitlist_submit', { lead_type: values.leadType })
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (submittingRef.current) {
      event.preventDefault()
      return
    }
    const nextErrors = validate(values)
    setErrors(nextErrors)
    const firstInvalid = FIELD_ORDER.find((f) => nextErrors[f])
    if (firstInvalid) {
      event.preventDefault()
      document.getElementById(fieldId(firstInvalid))?.focus()
      return
    }

    setStatus('submitting')
    submittingRef.current = true

    if (preview !== 'live') {
      event.preventDefault()
      window.setTimeout(() => finish(preview), 700)
      return
    }

    // Visitor id for Zoho SalesIQ, when that widget is present on the page.
    const uid = (window as ZohoWindow).$zoho?.salesiq?.visitor?.uniqueid?.()
    const ldt = formRef.current?.elements.namedItem('LDTuvid') as HTMLInputElement | null
    if (uid && ldt) ldt.value = uid

    // Let the browser post the form into the hidden iframe.
    timeoutRef.current = window.setTimeout(() => {
      if (submittingRef.current) finish('error')
    }, TIMEOUT_MS)
  }

  /** Fired when Zoho's response loads in the hidden iframe. */
  const onTargetLoad = () => {
    if (submittingRef.current) finish('success')
  }

  const reset = () => {
    setValues(EMPTY)
    setErrors({})
    setStatus('idle')
  }

  return { values, errors, status, setField, onSubmit, onTargetLoad, reset, fieldId, formRef, isPreview: preview !== 'live' }
}
